﻿import React, {useMemo} from 'react';
import {SecurSpaceReportContainer} from "../../components/common";
import {SecurSpaceReportTitle} from "../../components/common";
import {
  SupplierActivitySummarySortFilter
} from "../../components/supplier/activitySummaryReport/SupplierActivitySummarySortFilter";
import {
  SupplierActivitySummaryCardListWithLoader
} from "../../components/supplier/activitySummaryReport/SupplierActivitySummaryCardList";
import {useSupplierActivitySummaries} from "../../hooks/supplier/activitySummary/useSupplierActivitySummaries";
import {
  useSupplierActivitySummariesAggregated
} from "../../hooks/supplier/activitySummary/useSupplierActivitySummariesAggregated";
import {
  SupplierActivitySummariesGroupedWithLoader
} from "../../components/supplier/activitySummaryReport/SupplierActivitySummariesGrouped";
import {Grid, TablePagination} from "@mui/material";
import ReportDataSummary from "../../components/report/redesigned/ReportDataSummary";
import {useBindSearchParam} from "../../hooks/useBindSearchParam";
import {DateFormats} from "../../components/constants/securspace-constants";
import moment from "moment";
import {PersonAndBoxMessage} from "../../components/messages/PersonAndBoxMessage";
import {useSnackbar} from "../../context/snackbar-context";
import {SortValuesType} from "../../components/supplier/activitySummaryReport/SupplierActivitySummarySortInput";
import {SortDirType} from "../../types/common/SortDirType";
import {useSnackbarDisplayOnError} from "../../hooks/error/useSnackbarDisplayOnError";
import {useReportDataSummaryCollapseByIndex} from "../../hooks/report/useReportDataSummaryCollapseByIndex";
import DownloadCSVButton from "../../components/report/export/DownloadCSVButton";
import {getSupplierActivitySummaryDownload} from "../../requests/reports/supplier-activity-summary-requests";
import SecurSpaceContainer from "../../components/common/SecurSpaceContainer";
import {SecurSpaceReportPaginationContainer} from "../../components/common";

const DEFAULT_PAGE = 0;
const DEFAULT_ROWS_PER_PAGE = 10;

const SupplierActivitySummaryReport = () => {
  const { snackbarShowMessage } = useSnackbar();
  // Search params
  const [page, setPage] = useBindSearchParam('page', DEFAULT_PAGE.toString());
  const [rowsPerPage, setRowsPerPage] = useBindSearchParam('size', DEFAULT_ROWS_PER_PAGE.toString());

  const [date, setDate] = useBindSearchParam<string | ''>('date', moment().format(DateFormats.DAY).toString());
  const [sort, setSort] = useBindSearchParam<SortValuesType>('sortBy', 'LOCATION');
  const [sortDir, setSortDir] = useBindSearchParam<SortDirType>('sortDir', 'asc');
  const [locationId, setLocationId] = useBindSearchParam<string>('locationId', '');

  // Api hooks
  const { activitySummaries, pageInfo, isLoading, error } = useSupplierActivitySummaries({ page, rowsPerPage, date, sort, sortDir, locationId });
  const { aggregatedSummaries, isLoading: aggregatedSummariesLoading, error: aggregatedSummariesError} = useSupplierActivitySummariesAggregated({ date, locationId });

  // error hooks
  useSnackbarDisplayOnError({error, snackbarShowMessage });
  useSnackbarDisplayOnError({error: aggregatedSummariesError, snackbarShowMessage });

  // conditional display hooks
  // Not gated on `isLoading`: the previous payload survives a refetch, so
  // leaving this on keeps the empty state put instead of blinking out and
  // back on every filter change. It is undefined until the first load, so it
  // cannot flash before there is an answer.
  const shouldShowPersonAndBox = useMemo(() => {
    return !error && activitySummaries?.length === 0
  }, [error, activitySummaries]);

  // if they select a location there will only be 1 result, use its name for the header.
  // Ungated on `isLoading` for the same reason as the empty state above: the row is
  // retained across a refetch, so gating flipped the header to 'All' for the length of
  // every request and back again.
  const reportTitle = locationId && activitySummaries?.length === 1 ? activitySummaries[0].location : 'All'

  const showPagination = useMemo(() => {
    return !!(activitySummaries?.length && activitySummaries.length > 0);
  }, [activitySummaries])

  const {
    collapseByIndex,
    handleToggleCollapse,
    hasExpandedItems,
    setExpandedItems,
    setExpandedAll,
    handleExpandAll
  } = useReportDataSummaryCollapseByIndex({ data: activitySummaries });

  // change handlers
  const handlePageChange = (_: any, newPage: number) => {
    setPage(newPage?.toString());
  }

  const handleRowsPerPageChange = (event: any) => {
    setRowsPerPage(event.target?.value);
  }

  return <SecurSpaceContainer>
    <SecurSpaceReportContainer>
      <SecurSpaceReportTitle>Activity Summary</SecurSpaceReportTitle>
      <SupplierActivitySummarySortFilter sort={sort}
                                         onSortChange={setSort}
                                         sortDir={sortDir}
                                         onSortDirChange={setSortDir}
                                         locationId={locationId}
                                         onLocationChangeId={setLocationId}
                                         date={date}
                                         onDateChange={setDate}
      />
      {
        !shouldShowPersonAndBox && <SupplierActivitySummariesGroupedWithLoader isLoading={aggregatedSummariesLoading} aggregatedSummaries={aggregatedSummaries} />
      }
      {
        !shouldShowPersonAndBox && <ReportDataSummary
          count={pageInfo?.count || 0}
          title={reportTitle}
          isShowCollapseButton={true}
          hasExpandedItems={hasExpandedItems}
          setExpandedItems={setExpandedItems}
          setExpandedAll={setExpandedAll}
          handleExpandAll={handleExpandAll}
        >
          <Grid item xs lg={3}>
            <DownloadCSVButton
              className={'w-100'}
              title='Download Report'
              reportUrl={getSupplierActivitySummaryDownload({ locationId, date })}
            />
          </Grid>
        </ReportDataSummary>
      }
      {
        !error && <SupplierActivitySummaryCardListWithLoader isLoading={isLoading}
                                                             activitySummaries={activitySummaries}
                                                             collapseByIndex={collapseByIndex}
                                                             onToggleCollapse={handleToggleCollapse}
        />
      }
      {
        shouldShowPersonAndBox && <PersonAndBoxMessage />
      }
      {
        showPagination && <SecurSpaceReportPaginationContainer>
          <TablePagination
            component={'div'}
            count={pageInfo?.count || 0}
            page={pageInfo?.page || DEFAULT_PAGE}
            onPageChange={handlePageChange}
            rowsPerPage={pageInfo?.rowsPerPage || DEFAULT_ROWS_PER_PAGE}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </SecurSpaceReportPaginationContainer>
      }
    </SecurSpaceReportContainer>
  </SecurSpaceContainer>
}

export default SupplierActivitySummaryReport;
